import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import type { UserDisplaySettings } from '@/models/user';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast as sonnerToast } from 'sonner';
import { Palette, Save, Upload, Zap } from 'lucide-react';

type ActiveWorkspace = UserDisplaySettings['activeWorkspace'];

interface BrandIdentityCardProps {
  workspaceId: string;
  activeWorkspace: ActiveWorkspace;
  onUpdate: () => Promise<void>;
}

type PendingLogo = {
  base64: string;
  mimeType: string;
  fileName: string;
  previewUrl: string;
  sizeBytes: number;
} | null;

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

export const BrandIdentityCard: React.FC<BrandIdentityCardProps> = ({
  workspaceId,
  activeWorkspace,
  onUpdate,
}) => {
  const isPro = activeWorkspace.planDetails?.planId === 'pro';
  const [primaryColor, setPrimaryColor] = useState('#0f172a');
  const [pendingLogo, setPendingLogo] = useState<PendingLogo>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPrimaryColor(activeWorkspace.brandingPrimaryColor || '#0f172a');
  }, [activeWorkspace.brandingPrimaryColor]);

  useEffect(() => {
    return () => {
      if (pendingLogo?.previewUrl) URL.revokeObjectURL(pendingLogo.previewUrl);
    };
  }, [pendingLogo]);

  const handleLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_MIME_TYPES.has(file.type)) {
      sonnerToast.error('Logo must be PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      sonnerToast.error('Logo must be smaller than 2MB.');
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      const [, base64 = ''] = dataUrl.split(',', 2);
      if (!base64) {
        sonnerToast.error('Could not process logo image.');
        return;
      }
      setPendingLogo((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return {
          base64,
          mimeType: file.type,
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
          sizeBytes: file.size,
        };
      });
      sonnerToast.success('Logo ready to upload. Click Save Branding.');
    } catch (error) {
      console.error('Failed to read branding logo file', error);
      sonnerToast.error('Could not read logo file.');
    } finally {
      event.target.value = '';
    }
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!isPro) {
      sonnerToast.message('Upgrade to Growth to use custom checkout branding.');
      return;
    }
    setIsSaving(true);
    try {
      const updateFn = httpsCallable<
        {
          workspaceId: string;
          primaryColor?: string | null;
          logoDataBase64?: string | null;
          logoMimeType?: string | null;
          logoFileName?: string | null;
        },
        { success: true; branding: { primaryColor: string | null; logoFilename: string | null } }
      >(firebaseFunctions, 'updateStripeBranding');

      await updateFn({
        workspaceId,
        primaryColor,
        logoDataBase64: pendingLogo?.base64 || null,
        logoMimeType: pendingLogo?.mimeType || null,
        logoFileName: pendingLogo?.fileName || null,
      });
      sonnerToast.success('Branding synced to Stripe Checkout.');
      setPendingLogo((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
      await onUpdate();
    } catch (error: any) {
      sonnerToast.error(`Failed to update branding: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Palette className="h-4 w-4" />
              Brand Identity
            </CardTitle>
            <CardDescription className="text-slate-600">
              Sync your logo and primary color to Stripe-hosted checkout for a more trusted, premium buyer experience.
            </CardDescription>
          </div>
          <Badge variant={isPro ? 'default' : 'secondary'} className="rounded-full">{isPro ? 'Growth' : 'Upgrade Required'}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-[160px_1fr] md:items-center">
            <label htmlFor="branding-primary-color" className="text-sm font-medium text-slate-900">
              Primary color
            </label>
            <div className="flex items-center gap-3">
              <Input
                id="branding-primary-color"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={isSaving || !isPro}
                className="h-10 w-16 p-1"
                
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={isSaving || !isPro}
                placeholder="#0f172a"
                className="max-w-[180px]"
                
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[160px_1fr] md:items-start">
            <label htmlFor="branding-logo" className="pt-2 text-sm font-medium text-slate-900">
              Logo
            </label>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  id="branding-logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoChange}
                  disabled={isSaving || !isPro}
                  className="rounded-xl border-slate-200 bg-white file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-slate-700"
                />
              </div>
              <p className="text-xs text-muted-foreground">PNG, JPEG, or WebP. Max 2MB. Uploading replaces the previous Stripe logo.</p>
              {(activeWorkspace.brandingLogoFilename || pendingLogo) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-medium text-slate-700">
                    Current logo: {pendingLogo?.fileName || activeWorkspace.brandingLogoFilename}
                    {pendingLogo ? ' (pending upload)' : ''}
                  </p>
                  {pendingLogo && (
                    <img
                      src={pendingLogo.previewUrl}
                      alt="Brand logo preview"
                      className="mt-2 h-14 w-auto rounded bg-white p-1 shadow-sm"
                    />
                  )}
                  {pendingLogo && (
                    <p className="mt-1 text-xs text-slate-500">{Math.round(pendingLogo.sizeBytes / 1024)} KB</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isPro && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-700">
              Growth plan includes custom branded checkout. Upgrade to sync your studio logo and colors to Stripe Checkout.
            </div>
          )}

          <div className="rounded-xl border p-4" style={{ borderColor: `${primaryColor}33` }}>
            <p className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: primaryColor }}>
              Stripe Checkout Preview (Color)
            </p>
            <div className="mt-2 rounded-lg bg-white p-3 shadow-sm">
              <div className="h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
              <p className="mt-3 text-sm text-slate-800">Your client sees a more trusted, branded payment experience.</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSaving}>
              {isPro ? (
                <>
                  {pendingLogo ? <Upload className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Branding
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Upgrade to Growth
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
